import { useState, type CSSProperties } from 'react'
import { CHARACTERS, PROFESSIONS } from '../game/characters'
import { DEFAULT_SETTINGS, type PlayerConfig } from '../game/engine'
import { professionExpenses } from '../game/finance'
import type { GameSettings } from '../game/types'
import { roomFromUrl } from '../net/client'
import { ROOM_CODE_LENGTH } from '../net/protocol'
import { hasRoomToken, hasSave, useGame } from '../store/gameStore'
import { money } from '../util/format'
import { SoundToggle } from './SoundToggle'

const MAX_PLAYERS = 6

function defaultRow(i: number): PlayerConfig {
  return {
    name: i === 0 ? '' : CHARACTERS[i % CHARACTERS.length].name,
    isBot: i !== 0,
    characterId: CHARACTERS[i % CHARACTERS.length].id,
    professionId: PROFESSIONS[i % PROFESSIONS.length].id,
  }
}

export function Setup() {
  const { newGame, resume, createRoom, joinRoom, tryRejoin, net, dismissNetError } = useGame()
  const urlRoom = roomFromUrl()
  const [tab, setTab] = useState<'local' | 'online'>(urlRoom ? 'online' : 'local')
  const [rows, setRows] = useState<PlayerConfig[]>([defaultRow(0), defaultRow(1), defaultRow(2)])
  const [settings, setSettings] = useState<GameSettings>({ ...DEFAULT_SETTINGS })
  const [code, setCode] = useState(urlRoom ?? '')
  const canResume = hasSave()

  const patch = (i: number, p: Partial<PlayerConfig>) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...p } : row)))

  const takenChars = new Set(rows.map((r) => r.characterId))
  const you = rows[0]

  return (
    <div className="setup-scroll">
      <div className="setup">
        <header className="setup-hero">
          <span className="hero-badge">🚀 เกมเศรษฐี × Cashflow × ไซไฟ</span>
          <h1>
            MILLO
            <small>เกมเศรษฐีกระแสเงินสด</small>
          </h1>
          <p>
            เดินกระดานซื้อโฉนดแบบเกมเศรษฐีที่คุ้นเคย แต่ชนะด้วยกฎของ Kiyosaki — ทำ{' '}
            <b>Passive Income ให้แซงรายจ่ายรวม</b> เพื่อหลุดพ้นวงจรหนู ระหว่างทางเจอ
            พายุสุริยะ, AI แย่งงาน และการแช่แข็งไครโอ
          </p>
        </header>

        <div className="hero-tools">
          <SoundToggle />
        </div>

        <div className="mode-tabs">
          <button className={tab === 'local' ? 'on' : ''} onClick={() => setTab('local')}>
            🛋️ เล่นเครื่องเดียว
            <em className="faint tiny">ผลัดกันเล่น + บอท</em>
          </button>
          <button className={tab === 'online' ? 'on' : ''} onClick={() => setTab('online')}>
            🛸 เล่นกับเพื่อนออนไลน์
            <em className="faint tiny">ส่งลิงก์ชวน คนละเครื่อง</em>
          </button>
        </div>

        {net.error && (
          <div className="card net-error">
            <span>⚠️ {net.error}</span>
            <button className="btn btn-sm btn-ghost" onClick={dismissNetError}>
              ปิด
            </button>
          </div>
        )}

        {tab === 'online' && (
          <>
            <section className="card setup-card">
              <div className="panel-title">ตัวคุณ</div>
              <div className="rows">
                <SeatEditor row={you} onPatch={(p) => patch(0, p)} taken={new Set()} />
              </div>
            </section>

            <section className="card setup-card">
              <div className="panel-title">เข้าห้องเพื่อน</div>
              <div className="join-box">
                <input
                  className="code-input"
                  value={code}
                  placeholder="โค้ดห้อง"
                  maxLength={ROOM_CODE_LENGTH}
                  autoCapitalize="characters"
                  spellCheck={false}
                  onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && code.length === ROOM_CODE_LENGTH) {
                      joinRoom(code, you.name, you.characterId, you.professionId)
                    }
                  }}
                  aria-label="โค้ดห้อง"
                />
                <button
                  className="btn btn-primary btn-lg"
                  disabled={code.length !== ROOM_CODE_LENGTH}
                  onClick={() => joinRoom(code, you.name, you.characterId, you.professionId)}
                >
                  เข้าร่วม →
                </button>
              </div>
              {urlRoom && (
                <p className="faint tiny invite-hint">
                  ✨ เปิดจากลิงก์ชวนแล้ว — โค้ด <b>{urlRoom}</b> ถูกกรอกให้อัตโนมัติ กดเข้าร่วมได้เลย
                </p>
              )}
            </section>

            <div className="setup-go">
              <button
                className="btn btn-primary btn-lg"
                onClick={() => createRoom(you.name, you.characterId, you.professionId)}
              >
                🛸 สร้างห้องใหม่
              </button>
              {hasRoomToken() && (
                <button className="btn btn-ghost btn-lg" onClick={() => tryRejoin()}>
                  ↩ กลับเข้าห้องเดิม
                </button>
              )}
            </div>
          </>
        )}

        {tab === 'local' && (
          <>
        <section className="card setup-card">
          <div className="panel-title">
            <span>ผู้เล่น ({rows.length})</span>
            <span className="setup-card-actions">
              <button
                className="btn btn-sm"
                disabled={rows.length >= MAX_PLAYERS}
                onClick={() => setRows((r) => [...r, freshRow(r)])}
              >
                + เพิ่มผู้เล่น
              </button>
            </span>
          </div>

          <div className="rows">
            {rows.map((row, i) => (
              <SeatEditor
                key={i}
                row={row}
                index={i}
                taken={takenChars}
                onPatch={(p) => patch(i, p)}
                onRemove={rows.length > 2 ? () => setRows((r) => r.filter((_, idx) => idx !== i)) : undefined}
                showKind
              />
            ))}
          </div>
        </section>

        <section className="card setup-card">
          <div className="panel-title">ตั้งค่าเกม</div>
          <div className="settings">
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.globalEvents}
                onChange={(e) => setSettings((s) => ({ ...s, globalEvents: e.target.checked }))}
              />
              <span>
                🌍 <b>เหตุการณ์ไซไฟระดับโลก</b>
                <em className="faint tiny">พายุสุริยะ, AI แย่งงาน, ฟองสบู่คริปโตแตก — กระทบทุกคนพร้อมกัน</em>
              </span>
            </label>
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.personalDebuffs}
                onChange={(e) => setSettings((s) => ({ ...s, personalDebuffs: e.target.checked }))}
              />
              <span>
                🧊 <b>โทษรายตัว</b>
                <em className="faint tiny">แช่แข็งไครโอ, บัญชีถูกแฮก, หลุมดำจิ๋ว — ติดตัวเฉพาะคนที่จั่วได้</em>
              </span>
            </label>

            <label className="field">
              <span>
                🎯 <b>ความยากของเป้าหมาย</b>
                <em className="faint tiny">
                  ต้องทำ Passive Income ให้ถึง {settings.freedomMultiplier.toFixed(2)}× ของรายจ่ายรวม
                </em>
              </span>
              <div className="seg">
                {[
                  { v: 0.75, l: 'สั้น 0.75× · ~45 รอบ' },
                  { v: 1, l: 'มาตรฐาน 1× · ~70 รอบ' },
                  { v: 1.5, l: 'มาราธอน 1.5×' },
                ].map((o) => (
                  <button
                    key={o.v}
                    className={settings.freedomMultiplier === o.v ? 'on' : ''}
                    onClick={() => setSettings((s) => ({ ...s, freedomMultiplier: o.v }))}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </label>

            <label className="field">
              <span>
                ⚡ <b>ความเร็วบอท</b>
              </span>
              <div className="seg">
                {[
                  { v: 1500, l: 'ช้า' },
                  { v: 900, l: 'ปกติ' },
                  { v: 350, l: 'เร็ว' },
                ].map((o) => (
                  <button
                    key={o.v}
                    className={settings.botSpeed === o.v ? 'on' : ''}
                    onClick={() => setSettings((s) => ({ ...s, botSpeed: o.v }))}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </label>
          </div>
        </section>

        <div className="setup-go">
          <button className="btn btn-primary btn-lg" onClick={() => newGame(rows, settings, Date.now())}>
            🚀 เริ่มเกม
          </button>
          {canResume && (
            <button className="btn btn-ghost btn-lg" onClick={() => resume()}>
              ▶ เล่นต่อเกมเดิม
            </button>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  )
}

/** การ์ดตั้งค่าผู้เล่นหนึ่งคน ใช้ทั้งหน้าเล่นเครื่องเดียวและช่อง "ตัวคุณ" ของโหมดออนไลน์ */
function SeatEditor({
  row,
  index = 0,
  taken,
  onPatch,
  onRemove,
  showKind,
}: {
  row: PlayerConfig
  index?: number
  taken: Set<string>
  onPatch: (p: Partial<PlayerConfig>) => void
  onRemove?: () => void
  showKind?: boolean
}) {
  const prof = PROFESSIONS.find((p) => p.id === row.professionId) ?? PROFESSIONS[0]
  const chr = CHARACTERS.find((c) => c.id === row.characterId) ?? CHARACTERS[0]
  const expenses = professionExpenses(prof)

  return (
    <div className="setup-row" style={{ borderColor: `${chr.color}55` }}>
      <div className="setup-row-head">
        <span className="setup-ava" style={{ background: chr.color }}>
          {chr.emoji}
        </span>
        <input
          value={row.name}
          placeholder={chr.name}
          maxLength={14}
          onChange={(e) => onPatch({ name: e.target.value })}
          aria-label={`ชื่อผู้เล่น ${index + 1}`}
        />
        {showKind && (
          <div className="seg">
            <button className={!row.isBot ? 'on' : ''} onClick={() => onPatch({ isBot: false })}>
              🙋 คน
            </button>
            <button className={row.isBot ? 'on' : ''} onClick={() => onPatch({ isBot: true })}>
              🤖 บอท
            </button>
          </div>
        )}
        {onRemove && (
          <button className="btn btn-sm btn-ghost" onClick={onRemove} aria-label="ลบผู้เล่น">
            ✕
          </button>
        )}
      </div>

      <div className="chr-picker">
        {CHARACTERS.map((c) => (
          <button
            key={c.id}
            className={`chr${c.id === row.characterId ? ' on' : ''}`}
            style={{ '--tk': c.color } as CSSProperties}
            disabled={taken.has(c.id) && c.id !== row.characterId}
            title={`${c.name} — ${c.tagline}`}
            onClick={() => onPatch({ characterId: c.id })}
          >
            {c.emoji}
          </button>
        ))}
      </div>

      <label className="prof-select">
        <select value={row.professionId} onChange={(e) => onPatch({ professionId: e.target.value })}>
          {PROFESSIONS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.emoji} {p.name} — เงินเดือน {money(p.salary)}
            </option>
          ))}
        </select>
      </label>

      <div className="prof-info">
        <span>
          <em className="faint tiny">เงินเดือน</em>
          <b className="num good-text">{money(prof.salary)}</b>
        </span>
        <span>
          <em className="faint tiny">รายจ่าย</em>
          <b className="num bad-text">{money(expenses)}</b>
        </span>
        <span>
          <em className="faint tiny">เหลือต่อรอบ</em>
          <b className="num">{money(prof.salary - expenses)}</b>
        </span>
        <span>
          <em className="faint tiny">เงินสดเริ่มต้น</em>
          <b className="num">{money(prof.startingCash)}</b>
        </span>
      </div>
      <p className="perk">✨ {prof.perk}</p>
    </div>
  )
}

function freshRow(rows: PlayerConfig[]): PlayerConfig {
  const used = new Set(rows.map((r) => r.characterId))
  const chr = CHARACTERS.find((c) => !used.has(c.id)) ?? CHARACTERS[0]
  const usedProf = new Set(rows.map((r) => r.professionId))
  const prof = PROFESSIONS.find((p) => !usedProf.has(p.id)) ?? PROFESSIONS[0]
  return { name: chr.name, isBot: true, characterId: chr.id, professionId: prof.id }
}
